import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faLock, faArrowRight, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../hooks/useAuth";
import { firestoreService } from "../services/firebaseAuth";
import "./LoginPage.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Use the main auth hook for complete state management
  const { user, login: authLogin } = useAuth();

  useEffect(() => {
    const checkUserAndRedirect = async () => {
      if (user) {
        console.log('🔍 Checking user profile and teams...');
        
        // For backend auth users, redirect to dashboard
        if (user.isBackendAuth) {
          console.log('✅ Backend auth user, redirecting to dashboard');
          navigate("/dashboard", { replace: true });
          return;
        }
        
        // Check if user has selected a favorite team
        if (!user.firestoreUser?.teamSelected) {
          console.log('🏎️ No team selected, redirecting to team selection');
          navigate("/team-selection", { replace: true });
          return;
        }
        
        // For Firebase auth users, check Firestore
        if (!user.firestoreUser?.profileCompleted) {
          console.log('📝 Profile not completed, redirecting to complete profile');
          navigate("/complete-profile", { replace: true });
          return;
        }

        try {
          // Check if user has fantasy teams using Firestore
          const teams = await firestoreService.getUserTeams(user.uid);
          console.log('🏎️ User teams:', teams);
          
          if (teams.length > 0) {
            console.log('✅ User has teams, redirecting to dashboard');
            navigate("/dashboard", { replace: true });
          } else {
            console.log('➕ No teams found, redirecting to dashboard');
            navigate("/dashboard", { replace: true });
          }
        } catch (error) {
          console.error('❌ Error checking teams:', error);
          navigate("/dashboard", { replace: true });
        }
      }
    };

    checkUserAndRedirect();
  }, [user, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      console.log('📧 Attempting email login...');
      // Use the auth context login method with email credentials
      await authLogin("email", { email, password });
      // Navigation will be handled by the useEffect above
    } catch (err) {
      console.error('❌ Email login failed:', err);
      setError(err.message || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      setError("");

      console.log('🔐 Attempting Google login...');
      // Use the auth context login method for Google
      await authLogin("google");
      // Navigation will be handled by the useEffect above
    } catch (err) {
      console.error('❌ Google login failed:', err);
      setError(err.message || "An error occurred during login");
    } finally {
      setIsLoading(false);
    }
  };

  // If user is already authenticated, show loading
  if (user) {
    return (
      <div className="auth-loading-screen">
        <div className="loading-content">
          <div className="loading-spinner">🏎️</div>
          <p className="loading-text">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {/* Video Background */}
      <video className="auth-video-bg" autoPlay loop muted playsInline>
        <source src="/videos/f1-race.mp4" type="video/mp4" />
      </video>
      <div className="auth-video-overlay"></div>

      {/* Back to Home Button */}
      <button className="auth-back-btn" onClick={() => navigate('/')}>
        ← Back to Home
      </button>

      {/* Main Container */}
      <div className="auth-container">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <div className="auth-brand-content">
            <div className="brand-logo">
              <span className="brand-icon">🏎️</span>
              <span className="brand-text">F1 Fantasy</span>
            </div>
            
            <h1 className="brand-title">
              Welcome Back To
              <br />
              <span className="gradient-text">Your F1 Journey</span>
            </h1>
            
            <p className="brand-subtitle">
              Log in to manage your team, track your points, and compete with friends.
            </p>

            <div className="brand-features">
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>Manage Your Team</span>
              </div>
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>Track Your Points</span>
              </div>
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>Compete in Leagues</span>
              </div>
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>Make Predictions</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="auth-form-side">
          <div className="auth-form-container">
            <div className="auth-form-header">
              <h2 className="form-title">Welcome Back</h2>
              <p className="form-subtitle">Sign in to your account to continue</p>
            </div>

            {error && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* Google Login Button */}
            <button 
              className="auth-google-btn"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faGoogle} className="google-icon" />
              <span>Continue with Google</span>
            </button>

            <div className="auth-divider">
              <span className="divider-line"></span>
              <span className="divider-text">or sign in with email</span>
              <span className="divider-line"></span>
            </div>

            {/* Login Form */}
            <form className="auth-form" onSubmit={handleEmailLogin}>
              <div className="form-field">
                <label className="form-label">Email Address</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                  <input
                    type="email"
                    className="form-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">Password</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faLock} className="input-icon" />
                  <input
                    type="password"
                    className="form-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="field-action">
                  <Link to="/forgot-password" className="forgot-link">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={isLoading}
              >
                <span>{isLoading ? 'Signing in...' : 'Sign In'}</span>
                {!isLoading && <FontAwesomeIcon icon={faArrowRight} className="btn-arrow" />}
              </button>
            </form>

            <p className="auth-switch-text">
              Don't have an account?{" "}
              <button 
                className="auth-switch-link"
                onClick={() => navigate('/signup')}
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
